import type { CSSProperties } from 'react';
import {
  AIFI_CHART_PRIMARY_COLOR,
  AIFI_CHART_SECONDARY_COLOR,
  AIFI_NEGATIVE_COLOR,
  AIFI_ORG_COLOR,
} from '@/domains/pages/ai-proficiency/constants';

/**
 * Even width so the tick's edges land on whole pixels inside the 20px marker hit box, on
 * the same grid as the dot below it (see POWER_BAR_MARKER_DOT_SIZE_PX). A fractional width
 * snaps the painted tick off-center and the dot then no longer lines up with it.
 */
export const POWER_BAR_MARKER_LINE_WIDTH = 4;
/** Dot under the top-quartile / best-peer tick — matches the `size-1.5` legend swatch. */
export const POWER_BAR_MARKER_DOT_SIZE_PX = 6;
export const POWER_BAR_DOT_GAP_PX = 7;
export const POWER_BAR_HEIGHT_PX = 13;
export const POWER_BAR_MARKER_EXTENSION_PX = 4;
export const POWER_BAR_COMPACT_HEIGHT_PX = 10;
export const POWER_BAR_COMPACT_MARKER_EXTENSION_PX = 3;
export const POWER_BAR_BORDER_WIDTH_PX = 1;
/** Track background — matches ladder (chart 3) bars. */
export const POWER_BAR_TRACK_CLASS = 'bg-secondary/80';
/** Solid gray ticks on score and ladder power bars (not transparent). */
export const POWER_BAR_TICK_COLOR = '#8A8A8A';
export const POWER_BAR_TICK_COLOR_LIT = '#CFCFCF';
/**
 * Comparison markers on the score power bars (benchmark on the org page, median and
 * best peer on the segment pages). Near-white to match the over-time charts' benchmark
 * line; hover brightens to pure white.
 */
export const POWER_BAR_COMPARISON_MARKER_COLOR = AIFI_CHART_SECONDARY_COLOR;
export const POWER_BAR_COMPARISON_MARKER_COLOR_LIT = '#ffffff';

/** White median / org-average marker (chart 1 + upskilling drawer). */
export const POWER_BAR_MEDIAN_TICK_COLOR = '#FFFFFF';
export const POWER_BAR_MEDIAN_TICK_COLOR_LIT = '#CFCFCF';
export const POWER_BAR_PEER_TICK_WIDTH = 1.5;

export type PowerBarLayout = {
  trackHeightPx: number;
  markerExtensionPx: number;
  /** Distance from container top to track top — equals markerExtensionPx. */
  trackTopPx: number;
  /** Full marker tick height (extensions + track). */
  markerHeightPx: number;
  /** Minimum container height to fit markers and track. */
  containerHeightPx: number;
};

export const STANDARD_POWER_BAR_LAYOUT = getPowerBarLayout(false);
export const COMPACT_POWER_BAR_LAYOUT = getPowerBarLayout(true);

export function getPowerBarLayout(compact = false): PowerBarLayout {
  const trackHeightPx = compact ? POWER_BAR_COMPACT_HEIGHT_PX : POWER_BAR_HEIGHT_PX;
  const markerExtensionPx = compact ? POWER_BAR_COMPACT_MARKER_EXTENSION_PX : POWER_BAR_MARKER_EXTENSION_PX;
  const trackTopPx = markerExtensionPx;
  const markerHeightPx = markerExtensionPx + trackHeightPx + markerExtensionPx;

  return {
    trackHeightPx,
    markerExtensionPx,
    trackTopPx,
    markerHeightPx,
    containerHeightPx: markerHeightPx,
  };
}

export function powerBarMarkerPosition(value: number, max = 100): string {
  const ratio = max > 0 ? (value / max) * 100 : 0;
  return `${Math.min(100, Math.max(0, ratio))}%`;
}
/** @deprecated Use getPowerBarLayout(compact).containerHeightPx */
export function powerBarLayoutBottomSpacerPx(compact = false): number {
  return getPowerBarLayout(compact).containerHeightPx;
}

export function selectedValueTone(isAhead: boolean) {
  return isAhead
    ? { valueStyle: { color: AIFI_ORG_COLOR } as const }
    : { valueStyle: { color: AIFI_NEGATIVE_COLOR } as const };
}

/** Horizontal bar chart–style fill: translucent gradient + solid accent border. */
export function powerBarColoredFillStyle(color: string): CSSProperties {
  return {
    border: `${POWER_BAR_BORDER_WIDTH_PX}px solid ${color}`,
    background: `linear-gradient(90deg, ${color}05 0%, ${color}50 50%, ${color}99 100%)`,
    boxSizing: 'border-box',
  };
}

/** Border color for the 0–100 proficiency score bar. */
export function powerBarScoreBorderColor(): string {
  return AIFI_CHART_PRIMARY_COLOR;
}

export function powerBarScoreFillStyle(_score?: number): CSSProperties {
  return powerBarColoredFillStyle(AIFI_CHART_PRIMARY_COLOR);
}

/** Pie/doughnut-style accent: vivid border with a very dim fill. */
export function pieChartAccentStyle(color: string): CSSProperties {
  return {
    border: `${POWER_BAR_BORDER_WIDTH_PX}px solid ${color}`,
    backgroundColor: `${color}10`,
    boxSizing: 'border-box',
  };
}
